import { PrimaryButton, ReactIcons, SimpleButton } from "@kalyzee/kalyzee-ui";
import { MeetingOutputDTO, ROLE, SORT_ORDER, TimeEventOutputDTO, TimeEventQueryMeetingPopulate, TimeEventQueryPopulate, TimeEventSortMethod, TimeEventTargetModels } from "@kalyzee/kast-web-sdk/http";
import { SearchInput } from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { ErrorView } from "../../../../components/utils/ErrorView";
import { GlassDatePicker } from "../../../../components/utils/GlassDatePicker";
import { TimeEventsAgenda } from "../../../../components/utils/TimeEventsAgenda";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { getGlobalQueryLabel, NavigateGlobalQuery } from "../../../../helpers/navigation";
import { isRoleLessThan } from "../../../../helpers/roles";
import { useDelayedState } from "../../../../hooks/states";
import styles from "./list.module.css";
import { VodFiltersPopup } from "./VodFiltersPopup";

const formatDate = (date: Date) => date.toLocaleDateString(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const formatDateRange = (range: [Date | null, Date | null]): string | undefined => {
  const [start, end] = range;
  if (!start) return undefined;
  if (!end || start.getTime() === end.getTime()) return `Le ${formatDate(start)}`;
  return `Du ${formatDate(start)} au ${formatDate(end)}`;
};

export const VodsAgenda = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();

  const user = useAppStoreSelector(state => state.user);
  const isMobile = useAppStoreSelector(state => state.isMobile);

  const [selectedEventId, setSelectedEventId] = useState<string>();

  // Draft: bound to the inputs (name search, calendar), does not trigger any request.
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [search, setSearch] = useState<string>();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Applied: values actually used for the fetch. Committing the draft updates it.
  const [applied, setApplied] = useState<{ search?: string; dateRange: [Date | null, Date | null] }>({ dateRange: [null, null] });

  const defaultResult = useMemo(() => ({ fetching: false, events: undefined }), []);
  const [result, setResult] = useDelayedState<{ fetching: boolean, events: TimeEventOutputDTO[] | undefined }>(defaultResult);
  const [error, setError] = useState<any>();

  const sdkCtx = useSdkContext();

  const onPlayVod = (eventId: string) => {
    const selectedEvent = result.events?.find((event) => event.id === eventId);
    const selectedMeeting = selectedEvent ? (selectedEvent.target as MeetingOutputDTO) : undefined;
    if (selectedMeeting) {
      // Push (not replace): the replay opens as an overlay on this same page
      // via `?vodId=`, so the browser Back button closes it.
      pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, {
        queries: { [getGlobalQueryLabel(NavigateGlobalQuery.VOD_ID)]: selectedMeeting.id },
      });
    }
  };

  const loadEvents = useCallback(() => {
    const { search: effectiveSearch, dateRange: effectiveRange } = applied;
    const afterDate = effectiveRange[0];
    let beforeDate = effectiveRange[1] ?? afterDate;
    if (beforeDate) {
      beforeDate = new Date(beforeDate.getFullYear(), beforeDate.getMonth(), beforeDate.getDate(), 23, 59, 59);
    }
    setResult(prev => ({ ...prev, fetching: true }));
    sdkCtx.client
      .getHttpClient()
      .timeEvent.getAll({
        rooms: undefined,
        targets: [TimeEventTargetModels.Meeting],
        meetingFilter: {
          containsActors: user && isRoleLessThan(user.role, ROLE.TECHNICIAN) ? [user.id] : undefined,
          record: true,
          search: effectiveSearch ? { value: effectiveSearch, fields: ["name", "moderator"] } : undefined,
        },
        populate: [TimeEventQueryPopulate.ROOM_SUB_ORGANIZATION],
        meetingPopulate: [TimeEventQueryMeetingPopulate.ACTOR_IDENTITY],
        afterDate: afterDate ?? undefined,
        beforeDate: beforeDate ?? undefined,
        sortMethod: TimeEventSortMethod.START_DATETIME,
        sortOrder: SORT_ORDER.DESCENDING,
      })
      .then((r) => {
        setError(undefined);
        setResult({ fetching: false, events: r.data }, 500);
        if (r.data?.length) setSelectedEventId(r.data[0].id);
      })
      .catch((err) => {
        setError(err);
        setResult(prev => ({ ...prev, fetching: false }), 500);
      });
  }, [user, applied, sdkCtx.client]);

  // The fetch follows the applied state (and client/user). On mount, applied holds its initial value.
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Commits: push the current draft into the applied state.
  const applyDraft = () => setApplied({ search, dateRange });

  const onApplyFilters = (newSearch: string | undefined, newRange: [Date | null, Date | null]) => {
    setSearch(newSearch);
    setDateRange(newRange);
    setFiltersOpen(false);
    setApplied({ search: newSearch, dateRange: newRange });
  };

  const clearSearch = () => {
    setSearch(undefined);
    setApplied(prev => ({ ...prev, search: undefined }));
  };

  const clearDateRange = () => {
    setDateRange([null, null]);
    setApplied(prev => ({ ...prev, dateRange: [null, null] }));
  };

  const dateRangeLabel = formatDateRange(dateRange);
  const hasActiveFilters = !!search || !!dateRangeLabel;

  if (error) return <ErrorView retry={() => { loadEvents(); }} />;

  return (
    <div className={styles.eventsList}>
      {isMobile ? (
        <>
          <SimpleButton
            className={styles.filtersButton}
            onClick={() => setFiltersOpen(true)}
            label={
              <>
                <ReactIcons.Settings className={styles.filtersButtonIcon} />
                <span className={styles.filtersButtonText}>Définir les filtres</span>
                <ReactIcons.ArrowRight className={styles.filtersButtonArrow} />
              </>
            }
          />
          {hasActiveFilters && (
            <div className={styles.filterChips}>
              {search && (
                <span className={styles.filterChip}>
                  <span className={styles.filterChipKey}>Nom</span>
                  <span className={styles.filterChipText}>{search}</span>
                  <button
                    type="button"
                    className={styles.filterChipClose}
                    onClick={clearSearch}
                    aria-label="Retirer le filtre nom"
                  >
                    <ReactIcons.Close />
                  </button>
                </span>
              )}
              {dateRangeLabel && (
                <span className={styles.filterChip}>
                  <span className={styles.filterChipKey}>Date</span>
                  <span className={styles.filterChipText}>{dateRangeLabel}</span>
                  <button
                    type="button"
                    className={styles.filterChipClose}
                    onClick={clearDateRange}
                    aria-label="Retirer le filtre date"
                  >
                    <ReactIcons.Close />
                  </button>
                </span>
              )}
            </div>
          )}
          <PrimaryButton
            text={result.fetching ? "Recherche en cours ..." : "Rechercher"}
            className={clsx(styles.searchBtn, styles.searchBtnMobile)}
            onClick={applyDraft}
          />
          <VodFiltersPopup
            visible={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            search={search}
            dateRange={dateRange}
            onApply={onApplyFilters}
          />
        </>
      ) : (
        <div className={styles.searchPanel}>
          <div className={styles.searchContainer}>
            <div className={styles.searchItemContainer} style={{ flex: 1 }}>
              Recherche par nom du cours ou de l'expert
              <SearchInput
                setValue={setSearch}
                className={styles.search}
                nativeProps={{
                  onKeyDown: (ev) => {
                    if (ev.key === "Enter") {
                      applyDraft();
                    }
                  },
                  placeholder: "Jean Dupont, Mathématiques ...",
                }}
              />
            </div>
            <div className={styles.searchItemContainer}>
              Recherche par date
              <GlassDatePicker
                ready={!!dateRange[0]}
                selectsRange={true}
                startDate={dateRange[0] ?? undefined}
                endDate={dateRange[1] ?? undefined}
                onChange={setDateRange}
                isClearable={true}
                placeholderText="Afficher les calendrier"
              />
            </div>
          </div>
          <PrimaryButton
            text={result.fetching ? "Recherche en cours ..." : "Rechercher"}
            className={styles.searchBtn}
            onClick={applyDraft}
          />
        </div>
      )}
      <TimeEventsAgenda
        timeEvents={result.events ? result.events.filter((event) => (event.target ? true : false)) : undefined}
        placeholdersNb={3}
        selectedEventId={selectedEventId}
        onSelectEvent={setSelectedEventId}
        onJoinEvent={onPlayVod}
        kind="student"
        mode="replay"
      />
    </div>
  );
};
